import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, delay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { ApiResponse, Session } from '../interfaces';
import { CentralizedStockConfiguration, Configuration } from '../models';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  constructor(private apiService: ApiService) {}

  create(groupCode: string, data: any): Observable<Configuration> {
    return this.apiService
      .post(this.apiService.configuration.replace('{{groupCode}}', groupCode), {
        configName: data.name,
        configValue: '',
        configDescription: data.description,
        configMetadata: {
          check: data.check ? JSON.parse(data.check) : false,
          dispatchToday: data.check ? JSON.parse(data.check) : false,
          level: data.level ? JSON.parse(data.level) : false
        },
        active: data.status ? JSON.parse(data.status) : true,
      })
      .pipe(
        map((response: ApiResponse) => {
          const session: Session = JSON.parse(
            localStorage.getItem(environment.sessionAuth)
          );
          const configuration: Configuration = response.data;
          session.configurations[groupCode].push(configuration);
          localStorage.setItem(
            environment.sessionAuth,
            JSON.stringify(session)
          );
          return configuration;
        })
      );
  }

  read(groupCode: string): Observable<Configuration[]> {
    let session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (
      session &&
      session.configurations &&
      session.configurations[groupCode]
    ) {
      return of(session.configurations[groupCode]);
    } else {
      return this.apiService
        .get(this.apiService.configuration.replace('{{groupCode}}', groupCode))
        .pipe(
          map((response: ApiResponse) => {
            session = JSON.parse(localStorage.getItem(environment.sessionAuth));
            if (!session.configurations) {
              session.configurations = {};
            }
            if (response.data) {
              session.configurations[groupCode] = response.data;
              this.setSession(session);
            }
            return response.data;
          })
        );
    }
  }

  update(groupCode: string, data: any): Observable<Configuration> {
    return this.apiService
      .put(
        this.apiService.configuration.replace('{{groupCode}}', groupCode),
        data
      )
      .pipe(
        map((response: ApiResponse) => {
          const sessionString = localStorage.getItem(environment.sessionAuth);
          const session: Session = JSON.parse(sessionString);
          const configuration: Configuration = response.data;
          const configurationIndex = session.configurations[groupCode].findIndex(
            (c, i) => {
              const match = c.id.toString() === configuration.id.toString();
              return match;
            }
          );

          if (configurationIndex !== -1) {
            session.configurations[groupCode][configurationIndex] = configuration;
          }

          this.setSession(session);
          return configuration;
        })
      );
  }

  updateBulk(groupCode: string, data: Configuration[]): Observable<Configuration[]> {
    return this.apiService
      .put(
        this.apiService.configurationBulk.replace('{{groupCode}}', groupCode),
        data
      )
      .pipe(
        map((response: ApiResponse) => {
          const sessionString = localStorage.getItem(environment.sessionAuth);
          const session: Session = JSON.parse(sessionString);
          const configurations: Configuration[] = response.data;
          configurations.forEach(config => {
            const idx = session.configurations[groupCode]
              .findIndex(c => c.id.toString() === config.id.toString());
            if (idx !== -1) {
              session.configurations[groupCode][idx] = config;
            }
          });
          this.setSession(session);
          return configurations;
        })
      );
  }


  setSession(session: Session) {
    localStorage.setItem(environment.sessionAuth, JSON.stringify(session));
  }

  removeSession(groupCode: string) {
    const session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    delete session.configurations[groupCode];
    this.setSession(session);
  }

  getDispatchPrimary(): Observable<Configuration> {
    let session: Session = JSON.parse(
      localStorage.getItem(environment.sessionAuth)
    );
    if (
      session &&
      session.configurations &&
      session.configurations['DISPATCH_TYPE']
    ) {
      const list = session.configurations['DISPATCH_TYPE'];
      return of(list && list.length ? list.find(i => i.configMetadata && i.configMetadata.level) : null);
    } else {
      return this.read('DISPATCH_TYPE')
      .pipe(map(response => {
        return response.find(i => i.configMetadata && i.configMetadata.level);
      }));
    }
  }

  getConfigLogin(): Observable<Configuration> {
    return this.apiService
        .getWithoutInterceptor(this.apiService.configuration.replace('{{groupCode}}', "GENERAL_CONFIG"))
        .pipe(
          map((response: any) => {
            return response.body;
          })
        );
  }

  getPaymetVaidityDays(): Observable<any> {
    return this.apiService.get(`${environment.apiConfiguration}/group/PAYMENT_VALIDITY_DAYS`);
  }

  getDirectCreditConfig(): Observable<any> {
    return this.apiService.get(`${environment.apiConfiguration}/group/DIRECT_CREDIT_CONFIG`);
  }

  getIfTheCompanyHasCentralizedStockEnabled(): Observable<CentralizedStockConfiguration> {
    return this.apiService.get(`${this.apiService.configurationGroup}/CENTRALIZED_STOCK`)
      .pipe()
  }

  getLabelConfiguration(): Observable<Configuration> {
    return this.apiService.get(this.apiService.configuration.replace('{{groupCode}}', 'LABEL_CONFIG'))
  }

  /**
   * Consulta si la configuración ENABLE_ADD_QUOTATION_SERVICES está activa para la compañía actual.
   * Se utiliza para habilitar el modal de servicios asociados en cotizaciones.
   */
  getQuotationServicesEnabled(): Observable<boolean> {
    return this.read('GENERAL_CONFIG').pipe(
      map(configs => {
        if (!configs) return false;
        const config = configs.find(c => c.configName === 'ENABLE_ADD_QUOTATION_SERVICES');
        return config ? config.configValue === 'true' : false;
      })
    );
  }

}
